/**
 * XER Projects API Client
 * 
 * This module provides TypeScript interfaces and API functions
 * for interacting with the XER project management endpoints.
 */

import apiClient from './client';

// Types
export interface Project {
  proj_id: number;
  proj_name: string;
  proj_short_name: string;
  start_date?: string;
  end_date?: string;
  proj_status?: string;
  proj_orig_cost?: number;
  proj_curr_cost?: number;
  proj_orig_drtn_hr_cnt?: number;
  proj_curr_drtn_hr_cnt?: number;
}

export interface ProjectSummary {
  total_tasks: number;
  completed_tasks: number;
  critical_tasks: number;
  avg_progress: number;
  schedule_health: 'healthy' | 'at_risk' | 'behind';
}

export interface ProjectDashboard {
  project: Project;
  summary: ProjectSummary;
  critical_path_count: number;
  overdue_tasks: number;
  resource_count: number;
}

export interface Activity {
  task_id: number;
  task_name: string;
  task_code?: string;
  early_start_date?: string;
  early_finish_date?: string;
  late_start_date?: string;
  late_finish_date?: string;
  phys_complete_pct?: number;
  is_critical: boolean;
  progress_status: 'not_started' | 'in_progress' | 'completed' | 'overdue';
  duration_days: number;
}

export interface ResourceAssignment {
  task_id: number;
  task_name: string;
  rsrc_id: number;
  rsrc_name: string;
  rsrc_short_name?: string;
  target_qty?: number;
  remain_qty?: number;
  act_this_per_qty?: number;
}

export interface WBSItem {
  wbs_id: number;
  wbs_name: string;
  wbs_short_name?: string;
  level: number;
  parent_wbs_id?: number;
  total_tasks: number;
  completed_tasks: number;
  progress: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface ImportInfo {
  import_run_id: string;
  imported_at?: string;
  file_name?: string;
  parser_version?: string;
  row_counts: Record<string, number>;
}

// API Functions
export const xerProjectsApi = {
  // Projects (Phase 3: no import_id in query; use project_id and optional version_id only)
  async getProjects(page = 1, limit = 20, _importId?: string): Promise<PaginatedResponse<Project>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    const response = await apiClient.get(`/api/projects?${params}`);
    return response.data;
  },

  async getProject(projId: number, _importId?: string): Promise<Project> {
    const response = await apiClient.get(`/api/projects/${projId}`);
    return response.data;
  },

  async getProjectDashboard(projId: number, _importId?: string): Promise<ProjectDashboard> {
    const response = await apiClient.get(`/api/projects/${projId}/dashboard`);
    return response.data;
  },

  async getProjectSummary(projId: number, _importId?: string): Promise<ProjectSummary> {
    const response = await apiClient.get(`/api/projects/${projId}/summary`);
    return response.data;
  },

  // Activities
  async getActivities(
    projId: number,
    options: {
      page?: number;
      limit?: number;
      critical?: boolean;
      search?: string;
      version_id?: string;
      importId?: string;
    } = {}
  ): Promise<PaginatedResponse<Activity>> {
    const params = new URLSearchParams({
      page: (options.page || 1).toString(),
      limit: (options.limit || 50).toString(),
    });
    if (options.critical !== undefined) params.set('critical', options.critical.toString());
    if (options.search) params.set('search', options.search);
    if (options.version_id) params.set('version_id', options.version_id);
    if (options.importId) params.set('import_id', options.importId);
    const response = await apiClient.get(`/api/projects/${projId}/activities?${params}`);
    return response.data;
  },

  async getPredecessors(projId: number, taskId: number, _importId?: string): Promise<Activity[]> {
    const response = await apiClient.get(`/api/projects/${projId}/activities/${taskId}/predecessors`);
    return response.data;
  },

  // Resources
  async getResourceAssignments(
    projId: number,
    page = 1,
    limit = 100,
    _importId?: string
  ): Promise<PaginatedResponse<ResourceAssignment>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    const response = await apiClient.get(`/api/projects/${projId}/resources/assignments?${params}`);
    return response.data;
  },

  // Analytics
  async getProjectAnalytics(projId: number, _importId?: string): Promise<any> {
    const response = await apiClient.get(`/api/projects/${projId}/analytics`);
    return response.data;
  },

  // WBS
  async getWBSRollup(
    projId: number,
    page = 1,
    limit = 50,
    _importId?: string
  ): Promise<PaginatedResponse<WBSItem>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    const response = await apiClient.get(`/api/projects/${projId}/wbs?${params}`);
    return response.data;
  },

  // Data Management
  async listImports(): Promise<ImportInfo[]> {
    const response = await apiClient.get('/api/projects/imports');
    return response.data;
  },

  // Administration
  async clearCache(): Promise<{ message: string }> {
    const response = await apiClient.post('/api/projects/cache/clear');
    return response.data;
  },

  async getPerformanceMetrics(): Promise<any> {
    const response = await apiClient.get('/api/projects/performance/metrics');
    return response.data;
  },
};
